import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  Divider,
  LinearProgress,
  Tooltip,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Stack,
  Grid,
  Chip,
} from '@mui/material';
import {
  Refresh,
  CheckCircle,
  PlayCircle,
  HourglassEmpty,
  Warning,
  Close,
  ChevronRight,
} from '@mui/icons-material';
import { TimingTowerData, TimingTowerDay, TimingTowerCharacter } from '../../types/widget';
import { apiService } from '../../services/api';
import { formatVarianceLabel, getCallTimeVarianceMinutes } from '../../utils/time';

const TimingTowerWidget: React.FC = () => {
  const [data, setData] = useState<TimingTowerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<TimingTowerDay | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getTimingTowerData();
      if (response.success && response.data) {
        setData(response.data);
      } else {
        throw new Error(response.error || 'Failed to fetch data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenModal = (day: TimingTowerDay) => {
    setSelectedDay(day);
  };

  const handleCloseModal = () => {
    setSelectedDay(null);
  };

  const getDayIcon = (status: TimingTowerDay['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle color="success" />;
      case 'in_progress':
        return <PlayCircle color="primary" />;
      case 'critical':
        return <Warning color="error" />;
      case 'planned':
      default:
        return <HourglassEmpty color="disabled" />;
    }
  };

  // --- NEW: Helper function to render styled detail rows in the modal ---
  const renderDetailRow = (
    label: string,
    value: React.ReactNode,
    valueColor?: string
  ) => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 1.5,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 'bold', color: valueColor || 'text.primary' }}
      >
        {value}
      </Typography>
    </Box>
  );
  // --- END NEW ---

const STATUS_LABELS: Record<TimingTowerDay['status'], string> = {
  completed: 'Wrapped',
  in_progress: 'In Progress',
  planned: 'Planned',
  scheduled: 'Scheduled',
  critical: 'Needs Attention',
};

const formatDateDisplay = (
  value?: string | null,
  { includeTime = true }: { includeTime?: boolean } = {}
) => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return includeTime ? date.toLocaleString() : date.toLocaleDateString();
  }
  return value;
};

const normalizeCharacters = (
  characters: Array<TimingTowerCharacter | string> = []
): TimingTowerCharacter[] =>
  characters.map((character) =>
    typeof character === 'string'
      ? {
          characterId: null,
          characterName: character,
          actorName: null,
          callTime: null,
          actualTime: null,
          arrivalVarianceMinutes: null,
        }
      : character
  );

const derivePunctualitySummary = (characters: TimingTowerCharacter[]) => {
  if (!characters.length) {
    return undefined;
  }

  const stats = characters.reduce(
    (acc, character) => {
      const variance =
        character.arrivalVarianceMinutes ??
        getCallTimeVarianceMinutes(character.callTime, character.actualTime);
      if (variance === null || variance === undefined) {
        return acc;
      }
      if (variance <= 0) {
        acc.onTime += 1;
      } else {
        acc.late += 1;
      }
      acc.totalVariance += variance;
      acc.samples += 1;
      return acc;
    },
    { onTime: 0, late: 0, totalVariance: 0, samples: 0 }
  );

  return {
    onTimeCount: stats.onTime,
    lateCount: stats.late,
    avgVarianceMinutes:
      stats.samples > 0 ? Number((stats.totalVariance / stats.samples).toFixed(2)) : null,
  };
};

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data) {
    return <Alert severity="info">No timing tower data available.</Alert>;
  }

  return (
    <Paper
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#1A202C', // Darker background for the tower
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid #4A5568',
          backgroundColor: '#2D3748',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Timing Tower
          </Typography>
          <Tooltip title="Refresh Data">
            <IconButton onClick={fetchData} size="small">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={data.currentScriptVersion}
            size="small"
            sx={{ backgroundColor: '#4A5568' }}
          />
          {data.scriptColor && (
            <Chip
              label={data.scriptColor}
              size="small"
              sx={{
                backgroundColor: data.scriptColorHex || '#4A5568',
                color: ['WHITE', 'YELLOW', 'BUFF'].includes(data.scriptColor) ? '#1A202C' : '#FFFFFF',
                fontWeight: 'bold',
              }}
            />
          )}
        </Box>
      </Box>

      {/* Day List */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        <List disablePadding>
          {data.days.map((day) => {
            const isCurrentDay = data.currentDayNumber === day.dayNumber;
            return (
            <ListItem
              key={day.dayNumber}
              button
              onClick={() => handleOpenModal(day)}
              divider
              sx={{
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
                // Highlight current production day with gold/amber
                ...(isCurrentDay && {
                  backgroundColor: 'rgba(246, 173, 85, 0.15)',
                  borderLeft: '4px solid #F6AD55',
                }),
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  py: 1,
                }}
              >
                <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                  {getDayIcon(day.status)}
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Day {day.dayNumber} {isCurrentDay && <Chip label="TODAY" size="small" sx={{ ml: 1, backgroundColor: '#F6AD55', color: '#1A202C', fontWeight: 'bold', height: 20, fontSize: '0.65rem' }} />}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Scenes: {Array.isArray(day.scenes) ? day.scenes.join(', ') : (day.scenesPlanned || day.scenes || 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Pages: {day.plannedPages}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    ml: 1,
                  }}
                >
                  <LinearProgress
                    variant="determinate"
                    value={day.progressPercent}
                    sx={{
                      width: 50,
                      height: 6,
                      borderRadius: 3,
                      mb: 0.5,
                    }}
                    color={
                      day.status === 'critical'
                        ? 'error'
                        : 'primary'
                    }
                  />
                  <Typography variant="caption" color="text.secondary">
                    {day.actualPages} shot
                  </Typography>
                </Box>
                <ChevronRight color="disabled" sx={{ ml: 1 }} />
              </Box>
            </ListItem>
            );
          })}
        </List>
      </Box>

      {/* --- NEW: Day Summary Modal --- */}
      <Dialog
        open={Boolean(selectedDay)}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#2D3748',
            width: { xs: '95%', sm: 640, md: 780 },
          },
        }}
      >
        {selectedDay &&
          (() => {
            // Handle both array and number types for scenes
            const plannedScenes = Array.isArray(selectedDay.scenesPlanned) 
              ? selectedDay.scenesPlanned 
              : (Array.isArray(selectedDay.scenes) ? selectedDay.scenes : []);
            const shotScenes = Array.isArray(selectedDay.scenesShot) ? selectedDay.scenesShot : [];
            const plannedSceneCount = selectedDay.plannedScenesCount 
              ?? (typeof selectedDay.scenesPlanned === 'number' ? selectedDay.scenesPlanned : plannedScenes.length);
            const shotSceneCount = selectedDay.shotScenesCount 
              ?? (typeof selectedDay.scenesShot === 'number' ? selectedDay.scenesShot : shotScenes.length);
            const showShotDetails = selectedDay.status === 'completed' && shotSceneCount > 0;
            const statusLabel = STATUS_LABELS[selectedDay.status] || selectedDay.status.toUpperCase();
            const updateSource = selectedDay.lastUpdatedBy;
            const formattedReportedOn = formatDateDisplay(updateSource?.reportedOn, {
              includeTime: false,
            });
            const formattedUpdatedAt = formatDateDisplay(updateSource?.updatedAt);
            const rawCharacters = (selectedDay.characters ?? []) as Array<
              TimingTowerCharacter | string
            >;
            const normalizedCharacters = normalizeCharacters(rawCharacters);
            const characters = normalizedCharacters.slice(0, 5);
            const extraCharacters =
              normalizedCharacters.length > characters.length
                ? normalizedCharacters.length - characters.length
                : 0;
            const punctualitySummary =
              selectedDay.punctualitySummary || derivePunctualitySummary(characters);
            const locationStatusLabel =
              selectedDay.firstLocation?.status === 'INT' ? 'Interior' : 'Exterior';

            const getVarianceDisplay = (character: TimingTowerCharacter) => {
              const variance =
                character.arrivalVarianceMinutes ??
                getCallTimeVarianceMinutes(character.callTime, character.actualTime);
              return formatVarianceLabel(variance);
            };

            return (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              Day {selectedDay.dayNumber} Summary
              <IconButton
                aria-label="close"
                onClick={handleCloseModal}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <Close />
              </IconButton>
            </DialogTitle>

                <DialogContent dividers>
                  <Stack spacing={2}>
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Status
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {statusLabel}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {showShotDetails
                          ? `Pages shot ${selectedDay.actualPages} / ${selectedDay.plannedPages}`
                          : `Planned pages ${selectedDay.plannedPages}`}
                      </Typography>
                    </Stack>

                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Updated By
                      </Typography>
                      <Typography variant="body1">
                        {updateSource?.label || 'Not documented'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formattedReportedOn
                          ? `Reported ${formattedReportedOn}`
                          : 'Reported date not provided'}
                      </Typography>
                      {(formattedUpdatedAt || updateSource?.referenceId) && (
                        <Typography variant="caption" color="text.secondary">
                          {formattedUpdatedAt
                            ? `Ingested ${formattedUpdatedAt}`
                            : 'Ingested date not recorded'}
                          {updateSource?.referenceId ? ` · ${updateSource.referenceId}` : ''}
                        </Typography>
                      )}
                      {updateSource?.notes && (
                        <Typography variant="caption" color="text.secondary">
                          {updateSource.notes}
                        </Typography>
                      )}
                    </Stack>

                    <Divider />

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Planned</Typography>
                        <Typography variant="body1">{selectedDay.plannedPages} pages</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {plannedSceneCount} scenes
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {plannedScenes && plannedScenes.length > 0
                            ? plannedScenes.join(', ')
                            : `${plannedSceneCount} scenes scheduled`}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Shot</Typography>
                        {showShotDetails ? (
                          <>
                            <Typography variant="body1">
                              {selectedDay.actualPages} pages
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {shotSceneCount} scenes
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {shotScenes.length > 0 ? shotScenes.join(', ') : 'Scenes not logged yet'}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Shot pages and scenes will display once the day is wrapped.
                          </Typography>
                        )}
                      </Grid>
                    </Grid>

                    <Divider />

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        {renderDetailRow('Crew Call', selectedDay.crewCallTime)}
                        {renderDetailRow('First Shot', selectedDay.actualFirstShotTime)}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Stack spacing={0.5}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Set
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {selectedDay.firstLocation?.name || 'TBD'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {locationStatusLabel}
                          </Typography>
                        </Stack>
                        <Stack spacing={0.5} sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Weather
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {selectedDay.weather?.forecast || 'TBD'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {selectedDay.weather?.temp || 'N/A'}
                          </Typography>
                        </Stack>
                      </Grid>
                    </Grid>

                    <Divider />

                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Cast Call Times (Top 5)
                </Typography>
                      {characters.length ? (
                        <>
                          <Table size="small">
                  <TableHead>
                    <TableRow>
                                <TableCell sx={{ borderBottom: '1px solid #4A5568' }}>
                                  Character ID
                                </TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #4A5568' }}>
                        Character
                      </TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #4A5568' }}>
                        Actor
                      </TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #4A5568' }}>
                                  Scheduled
                                </TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #4A5568' }}>
                                  Actual
                      </TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #4A5568' }}>
                                  Variance
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                              {characters.map((char) => (
                                <TableRow key={`${char.characterName}-${char.callTime || 'na'}`}>
                                  <TableCell sx={{ borderBottom: '1px solid #4A5568' }}>
                                    {char.characterId || '—'}
                                  </TableCell>
                                  <TableCell sx={{ borderBottom: '1px solid #4A5568' }}>
                                    {char.characterName}
                                  </TableCell>
                                  <TableCell sx={{ borderBottom: '1px solid #4A5568' }}>
                                    {char.actorName || 'Not listed'}
                          </TableCell>
                                  <TableCell sx={{ borderBottom: '1px solid #4A5568' }}>
                                    {char.callTime || 'Not posted'}
                          </TableCell>
                                  <TableCell sx={{ borderBottom: '1px solid #4A5568' }}>
                                    {char.actualTime || 'Not reported'}
                          </TableCell>
                                  <TableCell sx={{ borderBottom: '1px solid #4A5568' }}>
                                    {getVarianceDisplay(char)}
                          </TableCell>
                        </TableRow>
                              ))}
                  </TableBody>
                </Table>
                          {punctualitySummary && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                              {punctualitySummary.onTimeCount} on time/early ·{' '}
                              {punctualitySummary.lateCount} late · Average variance{' '}
                              {formatVarianceLabel(punctualitySummary.avgVarianceMinutes)}
                            </Typography>
                          )}
                          {extraCharacters > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                              +{extraCharacters} additional character
                              {extraCharacters > 1 ? 's' : ''} not shown.
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Call sheet data not available for this day.
                        </Typography>
                      )}
              </Box>
                  </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseModal}>Close</Button>
            </DialogActions>
          </>
            );
          })()}
      </Dialog>
    </Paper>
  );
};

export default TimingTowerWidget;
